import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabaseClient';
import type { Customer } from '../modules/customer/types';

// ==================== 類型定義 ====================

/**
 * 客戶篩選條件
 */
interface CustomerFilter {
  searchTerm: string;
  filterLevel: string;
  filterStatus: string;
}

/**
 * 客戶統計資料
 */
interface CustomerStats {
  total: number;
  diamond: number;
  gold: number;
  silver: number;
  normal: number;
  problem: number;
  active: number;
  inactive: number;
  blocked: number;
}

/**
 * useCustomers Hook 返回值
 */
interface UseCustomersReturn {
  customers: Customer[];
  filteredCustomers: Customer[];
  stats: CustomerStats;
  filter: CustomerFilter;
  loading: boolean;
  setFilter: React.Dispatch<React.SetStateAction<CustomerFilter>>;
  refreshCustomers: () => Promise<void>;
}

// ==================== Hook 主體 ====================

/**
 * 客戶管理 Hook
 * 提供客戶列表、篩選、統計等功能
 */
export function useCustomers(): UseCustomersReturn {
  // ==================== 狀態管理 ====================
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  // loading 初始為 true：hook mount 後立即開始載入，避免空表閃爍
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<CustomerFilter>({
    searchTerm: '',
    filterLevel: 'all',
    filterStatus: 'all'
  });

  // ==================== 資料載入 ====================
  
  /**
   * 從資料庫載入客戶列表
   */
  const loadCustomers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          id,
          name,
          email,
          phone,
          company,
          member_level,
          status,
          discount,
          avatar_url,
          auth_user_id,
          total_orders,
          total_spent,
          created_at,
          tags,
          is_urent,
          tax_id
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mappedData = (data || []).map(customer => ({
        ...customer,
        avatar: customer.avatar_url || 'https://readdy.ai/api/search-image?query=professional%20portrait%20headshot%20clean%20background&width=100&height=100&seq=default&orientation=squarish',
        tags: customer.tags || []
      }));

      setCustomers(mappedData);
    } catch (error) {
      console.error('載入客戶列表失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 重新載入客戶列表（供外部呼叫）
   */
  const refreshCustomers = async () => {
    await loadCustomers();
  };

  // ==================== 生命週期 ====================
  
  useEffect(() => {
    loadCustomers();
  }, []);

  // ==================== 計算屬性：篩選後的客戶列表 ====================
  
  /**
   * 根據篩選條件過濾客戶列表
   */
  const filteredCustomers = useMemo((): Customer[] => {
    let result = [...customers];

    // 搜尋過濾（姓名、公司、電話、Email）
    if (filter.searchTerm && filter.searchTerm.trim() !== '') {
      const searchLower = filter.searchTerm.toLowerCase().trim();
      result = result.filter(customer => 
        customer.name.toLowerCase().includes(searchLower) ||
        customer.company?.toLowerCase().includes(searchLower) ||
        customer.phone?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower)
      );
    }

    // 會員等級過濾
    if (filter.filterLevel && filter.filterLevel !== 'all') {
      result = result.filter(customer => customer.member_level === filter.filterLevel);
    }

    // 狀態過濾
    if (filter.filterStatus && filter.filterStatus !== 'all') {
      result = result.filter(customer => customer.status === filter.filterStatus);
    }

    return result;
  }, [customers, filter]);

  // ==================== 計算屬性：統計資料 ====================
  
  /**
   * 計算客戶統計資料
   */
  const stats = useMemo((): CustomerStats => {
    return {
      total: customers.length,
      diamond: customers.filter(c => c.member_level === 'diamond').length,
      gold: customers.filter(c => c.member_level === 'gold').length,
      silver: customers.filter(c => c.member_level === 'silver').length,
      normal: customers.filter(c => c.member_level === 'normal').length,
      problem: customers.filter(c => c.member_level === 'problem').length,
      active: customers.filter(c => c.status === 'active').length,
      inactive: customers.filter(c => c.status === 'inactive').length,
      blocked: customers.filter(c => c.status === 'blocked').length
    };
  }, [customers]);

  // ==================== 返回值 ====================
  
  return {
    customers,
    filteredCustomers,
    stats,
    filter,
    loading,
    setFilter,
    refreshCustomers
  };
}
